import React, { useState, useEffect } from "react";
import ConfirmMessage from "../ModalFirmaElectronica/ConfirmMessage";
import axios from "../../../configApi/axiosConfig";
import { APIURL } from "../../../configApi/apiConfig";
import { SiLinkfire } from "react-icons/si";
import { BsPersonBadge, BsPersonBadgeFill } from "react-icons/bs";
import { PiSignatureLight, PiTimerLight, PiNewspaperBold, PiProhibitLight } from "react-icons/pi";

// Modal de firma electrónica específico para Banco Solidario (proveedor DataAgree /
// Security Data). Se separó de ModalFirmaElectronica para no seguir chocando en cada
// merge con los cambios del flujo genérico (TWS2 + Yupana), ver docs/proceso-firma-
// electronica-dataagree.md en FirmaElectronicaSecurityData.

// ─── Iconos utilitarios ───────────────────────────────────────────────────────

const CheckCircleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
    <circle cx="9" cy="9" r="8" stroke="currentColor" strokeWidth="1.5" />
    <path d="M5.5 9l2.5 2.5 4.5-5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const AlertCircleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
    <circle cx="9" cy="9" r="8" stroke="currentColor" strokeWidth="1.5" />
    <path d="M9 5.5v4M9 12h.01" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const SpinnerIcon = ({ size = 17 }) => (
  <svg className="animate-spin" width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeOpacity="0.25" />
    <path d="M12 2a10 10 0 0110 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
  </svg>
);

const CloseIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path d="M3 3l10 10M13 3L3 13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const FingerprintIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M12 2C8.13 2 5 5.13 5 9v3c0 4.42 3.58 8 8 8s8-3.58 8-8V9c0-3.87-3.13-7-7-7z" stroke="#fff" strokeWidth="1.6" strokeLinejoin="round" />
    <path d="M9 12c0-1.66 1.34-3 3-3s3 1.34 3 3" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" />
    <path d="M12 15v2" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" />
    <circle cx="12" cy="9" r="1" fill="#fff" />
  </svg>
);

const SendIcon = () => (
  <svg width="17" height="17" viewBox="0 0 17 17" fill="none" aria-hidden="true">
    <path d="M8.5 2.5v9M8.5 2.5L5 6M8.5 2.5L12 6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M2.5 12v.75A2.25 2.25 0 004.75 15h7.5A2.25 2.25 0 0014.5 12.75V12" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

// ─── Configuración de estados (alineada con firmaElectronicaIcons de ListadoSolicitud) ──
// 0=NO APLICA  1=APLICA/PENDIENTE  2=LINK ENVIADO  3=ACEPTADO
// 4=PRUEBA_VIDA_PROCESANDO  5=PRUEBA_VIDA  6=DOCUMENTO  7=RECHAZADO

const FIRMA_ESTADOS = {
  0: { label: "No aplica",              badge: "bg-slate-100 text-slate-500" },
  1: { label: "Pendiente",              badge: "bg-blue-100 text-blue-700" },
  2: { label: "Link Enviado",           badge: "bg-yellow-100 text-yellow-700" },
  3: { label: "Aceptado",              badge: "bg-green-100 text-green-700" },
  4: { label: "Procesando",            badge: "bg-red-100 text-red-700" },
  5: { label: "Prueba de Vida",        badge: "bg-red-100 text-red-700" },
  6: { label: "Completado",            badge: "bg-green-100 text-green-700" },
  7: { label: "Rechazado",             badge: "bg-red-100 text-red-700" },
};

// ─── Paneles de contenido por estado ─────────────────────────────────────────

function EstadoLinkEnviado() {
  return (
    <div className="flex flex-col items-center text-center gap-3 bg-yellow-50 border border-yellow-200 rounded-xl p-5">
      <div className="w-12 h-12 rounded-full bg-yellow-100 flex items-center justify-center">
        <SiLinkfire size={30} color="#eab308" />
      </div>
      <div>
        <p className="font-bold text-yellow-800 text-sm">Link ya enviado</p>
        <p className="text-xs text-yellow-700 mt-1 leading-relaxed">
          El enlace biométrico fue enviado al cliente y está pendiente de ser utilizado.
        </p>
      </div>
    </div>
  );
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Correo al que DataAgree enviará el enlace de firma. Solo se puede corregir
// con el permiso "REENVIAR FIRMA ELECTRONICA BANCO SOLIDARIO" (`editable`); sin
// él se muestra de solo lectura. `contacto` lo carga el modal desde
// Web_SolicitudGrande.
function CampoCorreoFirma({ contacto, email, setEmail, disabled, editable = true }) {
  if (!editable) {
    return (
      <div className="mb-4">
        <p className="block text-[11px] font-semibold text-slate-700 mb-1">Correo al que se enviará</p>
        <p className="w-full px-3 py-2 rounded-lg border border-slate-200 bg-slate-100 text-sm text-slate-700 break-all">
          {contacto.cargando ? "Cargando..." : contacto.emailOriginal || "Sin correo registrado"}
        </p>
        {contacto.celular && (
          <p className="text-[11px] text-slate-500 mt-2">
            También se notificará por WhatsApp al <span className="font-semibold">{contacto.celular}</span>.
          </p>
        )}
      </div>
    );
  }

  const emailLimpio = email.trim();
  const emailCambiado = emailLimpio !== contacto.emailOriginal;

  return (
    <div className="mb-4">
      <label htmlFor="firma-bs-email" className="block text-[11px] font-semibold text-slate-700 mb-1">
        Correo al que se enviará
      </label>
      <input
        id="firma-bs-email"
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        disabled={contacto.cargando || disabled}
        placeholder={contacto.cargando ? "Cargando..." : "correo@ejemplo.com"}
        className="w-full px-3 py-2 rounded-lg border border-slate-300 bg-white text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-slate-100"
      />
      {!contacto.cargando && emailLimpio && !EMAIL_REGEX.test(emailLimpio) && (
        <p className="text-[11px] text-rose-600 mt-1">Ingrese un correo válido.</p>
      )}
      {!contacto.cargando && !emailLimpio && contacto.emailOriginal && (
        <p className="text-[11px] text-rose-600 mt-1">Ingrese el correo del cliente.</p>
      )}
      {!contacto.cargando && emailLimpio && EMAIL_REGEX.test(emailLimpio) && emailCambiado && (
        <p className="text-[11px] text-amber-700 mt-1">
          El correo de la solicitud se actualizará a este nuevo valor.
        </p>
      )}
      {contacto.celular && (
        <p className="text-[11px] text-slate-500 mt-2">
          También se notificará por WhatsApp al <span className="font-semibold">{contacto.celular}</span>.
        </p>
      )}
    </div>
  );
}

// Reenvío del enlace de firma (solo con permiso "REENVIAR FIRMA ELECTRONICA
// BANCO SOLIDARIO" y enlace ya enviado). El backend cancela el enlace anterior
// y genera uno nuevo con el correo indicado.
function ReenviarEnlace({ contacto, email, setEmail, emailValido, isSubmitting, onReenviar }) {
  const bloqueado = isSubmitting || contacto.cargando || !emailValido;

  return (
    <div className="rounded-xl border border-yellow-200 bg-yellow-50/60 p-5 mt-4">
      <div className="flex items-center gap-2 mb-2">
        <SendIcon />
        <span className="text-[10px] font-semibold uppercase tracking-widest text-yellow-800">
          Reenviar enlace
        </span>
      </div>
      <p className="text-xs text-slate-600 leading-relaxed mb-4">
        Se anulará el enlace anterior y se enviará uno nuevo al cliente. Puede corregir el
        correo antes de reenviar.
      </p>

      <CampoCorreoFirma contacto={contacto} email={email} setEmail={setEmail} disabled={isSubmitting} />

      <button
        className={`w-full flex items-center justify-center gap-2.5 px-5 py-3 rounded-xl font-semibold text-sm transition-all duration-200 select-none ${
          bloqueado
            ? "bg-blue-600/60 text-white cursor-not-allowed"
            : "bg-blue-700 text-white hover:bg-blue-800 shadow-md hover:shadow-lg active:scale-[0.98] cursor-pointer"
        }`}
        onClick={onReenviar}
        disabled={bloqueado}
        aria-busy={isSubmitting}
      >
        {isSubmitting ? (
          <><SpinnerIcon /><span>Reenviando enlace...</span></>
        ) : (
          <><SendIcon /><span>Reenviar enlace</span></>
        )}
      </button>
    </div>
  );
}

function EstadoAceptado() {
  return (
    <div className="flex flex-col items-center text-center gap-3 bg-green-50 border border-green-200 rounded-xl p-5">
      <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
        <BsPersonBadge size={30} color="#22c55e" />
      </div>
      <div>
        <p className="font-bold text-green-800 text-sm">Firma aceptada</p>
        <p className="text-xs text-green-700 mt-1 leading-relaxed">
          El cliente aceptó el proceso de verificación de identidad exitosamente.
        </p>
      </div>
    </div>
  );
}

function EstadoPruebaVidaProcesando() {
  return (
    <div className="flex flex-col items-center text-center gap-4 bg-red-50 border border-red-200 rounded-xl p-5">
      <div className="relative w-14 h-14 flex items-center justify-center">
        <div className="absolute inset-0 rounded-full border-4 border-red-200 border-t-red-500 animate-spin" />
        <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
          <BsPersonBadgeFill size={22} color="#dc2626" />
        </div>
      </div>
      <div className="w-full">
        <p className="font-bold text-red-800 text-sm">Procesando prueba de vida</p>
        <p className="text-xs text-red-600 mt-1 leading-relaxed">
          La prueba de vida está siendo analizada. Por favor espere, esto puede tardar unos minutos.
        </p>
        <div className="mt-3 w-full bg-red-100 rounded-full h-1.5 overflow-hidden">
          <div
            className="h-full bg-red-400 rounded-full animate-pulse"
            style={{ width: '65%' }}
          />
        </div>
        <p className="text-[10px] text-red-400 mt-1.5 animate-pulse tracking-wide">Analizando datos biométricos...</p>
      </div>
    </div>
  );
}

function EstadoPruebaVida() {
  return (
    <div className="flex flex-col items-center text-center gap-4 bg-red-50 border border-red-200 rounded-xl p-5">
      <div className="relative w-14 h-14 flex items-center justify-center">
        <div className="absolute inset-0 rounded-full bg-red-200 animate-ping opacity-50" style={{ animationDuration: '1.8s' }} />
        <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center relative">
          <PiTimerLight size={30} color="#dc2626" />
        </div>
      </div>
      <div className="w-full">
        <p className="font-bold text-red-800 text-sm">Prueba de vida en curso</p>
        <p className="text-xs text-red-600 mt-1 leading-relaxed">
          El cliente está completando la prueba de vida biométrica. Esperando su respuesta.
        </p>
        <p className="text-[10px] text-red-400 mt-2 animate-pulse tracking-wide">Esperando al cliente...</p>
      </div>
    </div>
  );
}

function EstadoDocumento() {
  return (
    <div className="flex flex-col items-center text-center gap-3 bg-green-50 border border-green-200 rounded-xl p-5">
      <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
        <PiNewspaperBold size={30} color="#16a34a" />
      </div>
      <div>
        <p className="font-bold text-green-800 text-sm">Proceso completado exitosamente</p>
        <p className="text-xs text-green-700 mt-1 leading-relaxed">
          El documento ha sido firmado y todo el proceso biométrico fue completado con éxito.
        </p>
      </div>
    </div>
  );
}

function EstadoRechazado() {
  return (
    <div className="flex flex-col items-center text-center gap-3 bg-red-50 border border-red-200 rounded-xl p-5">
      <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
        <PiProhibitLight size={32} color="#dc2626" />
      </div>
      <div>
        <p className="font-bold text-red-800 text-sm">Verificación rechazada</p>
        <p className="text-xs text-red-700 mt-1 leading-relaxed">
          El proceso de verificación fue rechazado. Contacte a soporte para más información.
        </p>
      </div>
    </div>
  );
}

function EstadoDesconocido({ idFirmaElectronica }) {
  return (
    <div className="flex flex-col items-center text-center gap-3 bg-slate-50 border border-slate-200 rounded-xl p-5">
      <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center">
        <PiSignatureLight size={30} color="#9ca3af" />
      </div>
      <div>
        <p className="font-bold text-slate-700 text-sm">Estado no reconocido</p>
        <p className="text-xs text-slate-500 mt-1 leading-relaxed">
          Estado #{idFirmaElectronica}. Contacte a soporte técnico.
        </p>
      </div>
    </div>
  );
}

function EstadoPrefacturaRequerida() {
  return (
    <div className="flex flex-col items-center text-center gap-3 bg-amber-50 border border-amber-200 rounded-xl p-5">
      <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M12 9v4M12 17h.01" stroke="#D97706" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" stroke="#D97706" strokeWidth="1.6" strokeLinejoin="round" />
        </svg>
      </div>
      <div>
        <p className="font-bold text-amber-800 text-sm">Prefactura pendiente de aprobación</p>
        <p className="text-xs text-amber-700 mt-1 leading-relaxed">
          La prefactura debe ser aprobada antes de poder generar el link de firma digital.
        </p>
      </div>
    </div>
  );
}

// ─── Componente principal ─────────────────────────────────────────────────────

const CopyIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect x="9" y="9" width="13" height="13" rx="2" stroke="currentColor" strokeWidth="1.8"/>
    <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" stroke="currentColor" strokeWidth="1.8"/>
  </svg>
);

const ExternalLinkIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6M15 3h6v6M10 14L21 3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

function LinkBiometrico({ link, yaExistia }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(link).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className={`rounded-xl border p-4 mt-4 ${yaExistia ? "bg-amber-50 border-amber-200" : "bg-emerald-50 border-emerald-200"}`}>
      <p className={`text-[10px] font-bold uppercase tracking-widest mb-2 ${yaExistia ? "text-amber-700" : "text-emerald-700"}`}>
        {yaExistia ? "Link generado anteriormente" : "Link biométrico generado"}
      </p>
      <div className="flex items-center gap-2 bg-white rounded-lg border border-slate-200 px-3 py-2">
        <span className="flex-1 text-xs text-slate-700 truncate font-mono">{link}</span>
        <button
          onClick={handleCopy}
          title="Copiar link"
          className="flex-shrink-0 p-1.5 rounded-md text-slate-500 hover:text-slate-800 hover:bg-slate-100 transition-colors cursor-pointer"
        >
          {copied ? <CheckCircleIcon /> : <CopyIcon />}
        </button>
        <a
          href={link}
          target="_blank"
          rel="noreferrer"
          title="Abrir link"
          className="flex-shrink-0 p-1.5 rounded-md text-slate-500 hover:text-blue-700 hover:bg-blue-50 transition-colors"
        >
          <ExternalLinkIcon />
        </a>
      </div>
    </div>
  );
}

export function ModalFirmaElectronicaBancoSolidario({ data, isOpen, onClose, isPrefacturaAprobada, puedeReenviar = false }) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmReenvioOpen, setConfirmReenvioOpen] = useState(false);
  // Correo de destino del enlace (editable) y datos de contacto de la solicitud.
  const [email, setEmail] = useState("");
  const [contacto, setContacto] = useState({ cargando: false, emailOriginal: "", celular: "" });
  const [apiMessage, setApiMessage] = useState(null);
  const [apiMessageType, setApiMessageType] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [generatedLink, setGeneratedLink] = useState(null);
  const [linkYaExistia, setLinkYaExistia] = useState(false);
  // Se activa cuando la generación del link terminó con éxito, para bloquear
  // la UI y evitar que un doble click o un `data` desactualizado vuelvan a
  // disparar el proceso antes de que la pantalla se cierre.
  const [flowCompleted, setFlowCompleted] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setGeneratedLink(null);
      setLinkYaExistia(false);
      setApiMessage(null);
      setApiMessageType(null);
      setFlowCompleted(false);
      setConfirmReenvioOpen(false);
    }
  }, [isOpen]);

  // Al abrir, carga el correo y celular a los que saldría el enlace.
  useEffect(() => {
    if (!isOpen || !data?.sCre_SolicitudWeb) return;
    let cancelado = false;
    setEmail("");
    setContacto({ cargando: true, emailOriginal: "", celular: "" });
    axios
      .get(APIURL.get_contacto_firma_banco_solidario(data.sCre_SolicitudWeb))
      .then(({ data: res }) => {
        if (cancelado) return;
        setEmail(res?.email ?? "");
        setContacto({ cargando: false, emailOriginal: res?.email ?? "", celular: res?.celular ?? "" });
      })
      .catch(() => {
        if (!cancelado) setContacto({ cargando: false, emailOriginal: "", celular: "" });
      });
    return () => { cancelado = true; };
  }, [isOpen, data?.sCre_SolicitudWeb]);

  // Sin el permiso el correo no se puede cambiar: se envía siempre al de la solicitud.
  const emailLimpio = puedeReenviar ? email.trim() : contacto.emailOriginal;
  // Vacío solo se acepta si la solicitud tampoco tenía correo (se notifica por WhatsApp).
  const emailValido =
    !puedeReenviar || (emailLimpio ? EMAIL_REGEX.test(emailLimpio) : !contacto.emailOriginal);
  const envioBloqueado = isSubmitting || contacto.cargando || !emailValido;

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [isOpen, onClose]);

  const handleConfirm = async () => {
    if (isSubmitting || flowCompleted) return;

    setConfirmOpen(false);
    setIsSubmitting(true);
    setApiMessage(null);
    setGeneratedLink(null);
    try {
      const response = await axios.post(APIURL.post_firma_banco_solidario(), {
        sCre_SolicitudWeb: data?.sCre_SolicitudWeb,
        email: emailLimpio,
      });
      const resData = response?.data;
      const yaExistia = resData?.yaExistia === true;
      const link = resData?.link ?? null;

      setApiMessage(resData?.message || "El link biométrico fue generado y enviado correctamente.");
      setApiMessageType("success");
      setLinkYaExistia(yaExistia);
      if (link) setGeneratedLink(link);
      setFlowCompleted(true);
    } catch (err) {
      setApiMessage(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          "No fue posible generar el link biométrico. Intente nuevamente."
      );
      setApiMessageType("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirmReenvio = async () => {
    setConfirmReenvioOpen(false);
    if (isSubmitting || flowCompleted) return;

    setIsSubmitting(true);
    setApiMessage(null);
    try {
      const response = await axios.post(APIURL.post_reenviar_firma_banco_solidario(), {
        sCre_SolicitudWeb: data?.sCre_SolicitudWeb,
        email: emailLimpio,
      });
      setApiMessage(response?.data?.message || "El enlace de firma fue reenviado correctamente.");
      setApiMessageType("success");
      setFlowCompleted(true);
    } catch (err) {
      setApiMessage(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          "No fue posible reenviar el enlace. Intente nuevamente."
      );
      setApiMessageType("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const firmaId = data?.idFirmaElectronica;
  const firmaIdEfectivo = flowCompleted ? 2 : firmaId;
  const estadoInfo = FIRMA_ESTADOS[firmaIdEfectivo];

  const renderContenido = () => {
    switch (firmaIdEfectivo) {
      case 1:
        if (!isPrefacturaAprobada) {
          return <EstadoPrefacturaRequerida />;
        }
        return (
          <div className="rounded-xl border border-blue-100 bg-blue-50/60 p-5">
            <div className="flex items-center gap-2 mb-2">
              <PiSignatureLight size={16} color="#1D4ED8" aria-hidden="true" />
              <span className="text-[10px] font-semibold uppercase tracking-widest text-blue-700">
                Link biométrico disponible
              </span>
            </div>
            <h3 className="text-sm font-bold text-slate-800 mb-2">
              Envío del enlace de verificación
            </h3>
            <p className="text-xs text-slate-600 leading-relaxed mb-5">
              Se generará y enviará un enlace biométrico al cliente para verificar su identidad
              y completar la firma digital. El titular debe tener su{" "}
              <strong className="text-slate-800">cédula física disponible</strong>.
            </p>
            <CampoCorreoFirma contacto={contacto} email={email} setEmail={setEmail} disabled={isSubmitting} editable={puedeReenviar} />
            <button
              className={`w-full flex items-center justify-center gap-2.5 px-5 py-3 rounded-xl font-semibold text-sm transition-all duration-200 cursor-pointer select-none ${
                envioBloqueado
                  ? "bg-blue-600/70 text-white cursor-not-allowed"
                  : "bg-blue-700 text-white hover:bg-blue-800 shadow-md hover:shadow-lg active:scale-[0.98]"
              }`}
              onClick={() => setConfirmOpen(true)}
              disabled={envioBloqueado}
              aria-busy={isSubmitting}
            >
              {isSubmitting ? (
                <><SpinnerIcon /><span>Generando enlace...</span></>
              ) : (
                <><SendIcon /><span>Generar link biométrico</span></>
              )}
            </button>
          </div>
        );
      case 2:
        return (
          <>
            <EstadoLinkEnviado />
            {puedeReenviar && firmaId === 2 && !flowCompleted && (
              <ReenviarEnlace
                contacto={contacto}
                email={email}
                setEmail={setEmail}
                emailValido={emailValido}
                isSubmitting={isSubmitting}
                onReenviar={() => setConfirmReenvioOpen(true)}
              />
            )}
          </>
        );
      case 3: return <EstadoAceptado />;
      case 4: return <EstadoPruebaVidaProcesando />;
      case 5: return <EstadoPruebaVida />;
      case 6: return <EstadoDocumento />;
      case 7:
        return (
          <div className="rounded-xl border border-red-200 bg-red-50/60 p-5">
            <div className="flex items-center gap-2 mb-2">
              <PiProhibitLight size={16} color="#dc2626" aria-hidden="true" />
              <span className="text-[10px] font-semibold uppercase tracking-widest text-red-700">
                Verificación rechazada / caducada
              </span>
            </div>
            <h3 className="text-sm font-bold text-slate-800 mb-2">
              Reenviar enlace de verificación
            </h3>
            <p className="text-xs text-slate-600 leading-relaxed mb-5">
              El proceso anterior fue <strong className="text-red-700">rechazado o caducó</strong>.
              Puede generar un nuevo enlace biométrico para que el cliente complete la verificación nuevamente.
            </p>
            <CampoCorreoFirma contacto={contacto} email={email} setEmail={setEmail} disabled={isSubmitting} editable={puedeReenviar} />
            <button
              className={`w-full flex items-center justify-center gap-2.5 px-5 py-3 rounded-xl font-semibold text-sm transition-all duration-200 cursor-pointer select-none ${
                envioBloqueado
                  ? "bg-red-600/70 text-white cursor-not-allowed"
                  : "bg-red-700 text-white hover:bg-red-800 shadow-md hover:shadow-lg active:scale-[0.98]"
              }`}
              onClick={() => setConfirmOpen(true)}
              disabled={envioBloqueado}
              aria-busy={isSubmitting}
            >
              {isSubmitting ? (
                <><SpinnerIcon /><span>Generando enlace...</span></>
              ) : (
                <><SendIcon /><span>Generar nuevo link biométrico</span></>
              )}
            </button>
          </div>
        );
      default: return <EstadoDesconocido idFirmaElectronica={firmaId} />;
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm px-4 py-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby="firma-modal-bs-title"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md relative overflow-hidden border border-slate-200 max-h-[90vh] flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 bg-gradient-to-r from-blue-900 via-blue-800 to-indigo-700">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-xl bg-white/15 border border-white/25 flex items-center justify-center flex-shrink-0">
              <FingerprintIcon />
            </div>
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-widest text-blue-200/80 font-medium">
                Verificación de identidad · Banco Solidario
              </p>
              <h2 id="firma-modal-bs-title" className="text-base font-bold text-white leading-tight truncate">
                Firma Electrónica
              </h2>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-lg flex items-center justify-center text-white/70 hover:text-white hover:bg-white/10 transition-colors cursor-pointer flex-shrink-0 ml-3"
            aria-label="Cerrar modal"
          >
            <CloseIcon />
          </button>
        </div>

        {/* Titular + badge de estado */}
        <div className="px-6 py-4 bg-slate-50 border-b border-slate-200">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 mb-0.5">
                Titular
              </p>
              <p className="text-sm font-bold text-slate-800 leading-snug truncate">
                {data?.nombre || "—"}
              </p>
              <p className="text-xs text-slate-500 mt-0.5">
                C.I. <span className="font-semibold text-slate-700">{data?.cedula || "—"}</span>
              </p>
            </div>
            <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-slate-200 shadow-sm text-xs text-slate-600">
                <span className="text-slate-400 font-normal">Nro.</span>
                <span className="font-bold text-slate-800">#{data?.NumeroSolicitud || "—"}</span>
              </span>
              {estadoInfo && (
                <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide ${estadoInfo.badge}`}>
                  {estadoInfo.label}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Feedback API */}
        {apiMessage && (
          <div className="mx-6 mt-5">
            <div
              role="alert"
              aria-live="polite"
              className={`flex items-start gap-3 rounded-xl px-4 py-3.5 text-sm leading-snug border ${
                apiMessageType === "success"
                  ? "bg-emerald-50 border-emerald-200 text-emerald-800"
                  : "bg-rose-50 border-rose-200 text-rose-800"
              }`}
            >
              <span className="flex-shrink-0 mt-0.5">
                {apiMessageType === "success" ? <CheckCircleIcon /> : <AlertCircleIcon />}
              </span>
              <p className="flex-1 font-medium">{apiMessage}</p>
              <button
                onClick={() => { setApiMessage(null); setApiMessageType(null); setGeneratedLink(null); }}
                className="flex-shrink-0 opacity-60 hover:opacity-100 transition-opacity cursor-pointer"
                aria-label="Cerrar mensaje"
              >
                <CloseIcon />
              </button>
            </div>
            {/* Link biométrico generado */}
            {generatedLink && (
              <LinkBiometrico link={generatedLink} yaExistia={linkYaExistia} />
            )}
          </div>
        )}

        {/* Contenido según estado — scrollable si el contenido es largo */}
        <div className="px-6 py-6 overflow-y-auto flex-1">
          {renderContenido()}
        </div>
      </div>

      <ConfirmMessage
        isOpen={confirmOpen}
        onConfirm={handleConfirm}
        onCancel={() => setConfirmOpen(false)}
        message={`¿Está seguro que desea generar el link biométrico${emailLimpio ? ` y enviarlo a ${emailLimpio}` : ""}? Recuerde que el cliente debe contar con su cédula física.`}
      />

      <ConfirmMessage
        isOpen={confirmReenvioOpen}
        onConfirm={handleConfirmReenvio}
        onCancel={() => setConfirmReenvioOpen(false)}
        message={`¿Reenviar el enlace de firma${emailLimpio ? ` a ${emailLimpio}` : ""}? El enlace anterior dejará de funcionar.`}
      />
    </div>
  );
}
